import Modal from '@/shared/ui/Modal';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useOnboarding } from './context';
import { AlphaViz, CapitalViz } from './vizzes';

// Desktop copy is canonical for all breakpoints.
export default function WelcomeModal() {
  const { chooseTrader, chooseStaker, dismiss } = useOnboarding();
  const [hovered, setHovered] = useState<'trader' | 'staker' | null>(null);
  // Pure transform — the hover emphasis must never change layout (flex-grow
  // rewraps text and makes the whole modal jump).
  const scale = (self: 'trader' | 'staker') =>
    hovered === self ? 1.02
    : hovered === null ? 1
    : 0.98;

  return (
    // ✕ (closeModal) is the only dismiss — backdrop clicks must not close.
    <Modal closeModal={dismiss} closeOnOverlayClick={false} sheet>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="mx-auto w-235 max-w-[92vw] p-2xl text-gray-text max-md:w-full max-md:max-w-full max-md:p-l md:p-3xl"
      >
        <p className="text-center text-body-md font-medium text-purple-default max-md:text-left">
          Welcome to Score Arena by Santiment!
        </p>
        <h2 className="mt-xs text-center text-h1 font-bold max-md:text-left max-md:text-h2">
          Pick a flow to see features. You can use both within one account.
        </h2>
        <p className="mt-xs text-center text-body-md text-gray max-md:text-left">
          Let’s take a brief tour to get you started.
        </p>
        <div className="mt-2xl flex flex-col gap-l md:flex-row">
          <PathCard
            eyebrow="Trader"
            eyebrowColor="text-purple-default"
            title="Architect your Alpha"
            viz={<AlphaViz />}
            body="Publish signals, prove your edge, and climb the leagues. Hold 60%+ accuracy to unlock larger allocations and attract backer capital."
            cta="Start providing signals"
            ctaClass="bg-purple-default hover:bg-purple-hover"
            ringClass="ring-purple-default/25"
            hovered={hovered === 'trader'}
            scale={scale('trader')}
            onHover={(h) => setHovered(h ? 'trader' : null)}
            onClick={chooseTrader}
          />
          <PathCard
            eyebrow="Backer"
            eyebrowColor="text-green-default"
            title="Scale your Capital"
            viz={<CapitalViz />}
            body="Back top-performing providers with SAN and earn weekly rewards. Diversify across the arena and compound your returns automatically."
            cta="Browse Candidates"
            ctaClass="bg-green-default hover:bg-green-hover"
            ringClass="ring-green-default/25"
            hovered={hovered === 'staker'}
            scale={scale('staker')}
            onHover={(h) => setHovered(h ? 'staker' : null)}
            onClick={chooseStaker}
          />
        </div>
      </motion.div>
    </Modal>
  );
}

function PathCard({
  eyebrow,
  eyebrowColor,
  title,
  viz,
  body,
  cta,
  ctaClass,
  ringClass,
  hovered,
  scale,
  onHover,
  onClick,
}: {
  eyebrow: string;
  eyebrowColor: string;
  title: string;
  viz: ReactNode;
  body: string;
  cta: string;
  ctaClass: string;
  ringClass: string;
  hovered: boolean;
  scale: number;
  onHover: (hovered: boolean) => void;
  onClick: () => void;
}) {
  return (
    <div
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      style={{ transform: `scale(${scale})` }}
      className={`group flex flex-1 basis-0 flex-col rounded-md border border-gray-5 p-l transition-[transform,box-shadow] duration-300 ease-out ${
        hovered ? `shadow-lg ring-1 ${ringClass}` : ''
      }`}
    >
      <p className={`text-body-sm font-medium ${eyebrowColor}`}>{eyebrow}</p>
      <p className="text-h2 font-bold">{title}</p>
      <div className="mt-m">{viz}</div>
      <p className="mt-m flex-1 text-body-md text-gray">{body}</p>
      <button
        type="button"
        onClick={onClick}
        className={`mt-l btn-reset flex h-11 w-full items-center justify-center gap-s rounded-sm text-body-md font-semibold text-gray-inverse ${ctaClass}`}
      >
        {cta}
        <ArrowRight
          size={16}
          className="transition-transform group-hover:translate-x-0.5"
        />
      </button>
    </div>
  );
}
